import {FC, FocusEvent, MouseEvent, useEffect, useRef, useState} from 'react'

import {ResourceSubmenuItem, useHeaderContent} from '@/content/header'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {analytics} from '@/utils/tracking/analytics'

import {NavigationLink} from './links/NavigationLink'
import {SubmenuLink} from './links/SubmenuLink'
import {ResourcesSubmenu} from './submenu/ResourcesSubmenu'
import {ServicesSubmenu} from './submenu/ServicesSubmenu'

type HeaderNavigationProps = {
  closeMobileNav?: () => void
  resourcesSubmenuItems?: ResourceSubmenuItem[]
  isMobile?: boolean
}

export const HeaderNavigation: FC<React.PropsWithChildren<HeaderNavigationProps>> = ({
  closeMobileNav,
  resourcesSubmenuItems,
  isMobile,
}) => {
  const {t} = useTranslation('page')
  const {services, pricing, about, resources} = useHeaderContent().menu.navigation

  const [activeSubmenu, setActiveSubmenu] = useState('')
  const [isSwitchingSubmenu, setIsSwitchingSubmenu] = useState(false)
  const submenuPanelHeight = useRef(0)
  const navRef = useRef<HTMLElement>(null)

  const closeDesktopNavigation = () => {
    setActiveSubmenu('')
    setIsSwitchingSubmenu(false)
  }
  const closeMobileNavigation = () => closeMobileNav && closeMobileNav()

  const hideTimeout = useRef<NodeJS.Timeout | null>(null)

  const showSubmenu = (submenu: string, trackedEvent?: string) => {
    if (hideTimeout.current) {
      clearTimeout(hideTimeout.current)
      hideTimeout.current = null
    }
    // Re-entering the open submenu only cancels the hide. Resetting isSwitching here would restart
    // the panel's fade-in.
    if (activeSubmenu === submenu) {
      return
    }
    setIsSwitchingSubmenu(activeSubmenu !== '' && activeSubmenu !== submenu)
    setActiveSubmenu(submenu)
    if (trackedEvent) analytics.track(trackedEvent)
  }

  const hideSubmenu = () => (hideTimeout.current = setTimeout(closeDesktopNavigation, 150))

  const handleDesktopClick = (
    event: MouseEvent<HTMLElement>,
    submenu: string,
    trackedEvent?: string,
  ) => {
    if (activeSubmenu !== submenu) {
      showSubmenu(submenu, trackedEvent)
      return
    }
    // Mouse and touch users already opened it by hovering, so only a keyboard press (detail 0)
    // closes it. Otherwise a tap would open and close it at once.
    if (event.detail === 0) {
      closeDesktopNavigation()
    }
  }

  // Tabbing out of the open submenu closes it, so it doesn't cover the content focus moves to.
  // No relatedTarget means a click on a non-focusable spot, which must not close it under the mouse.
  const handleDesktopBlur = (event: FocusEvent<HTMLLIElement>, submenu: string) => {
    const nextFocused = event.relatedTarget
    if (activeSubmenu === submenu && nextFocused && !event.currentTarget.contains(nextFocused)) {
      closeDesktopNavigation()
    }
  }

  // Escape closes the open desktop submenu
  useEffect(() => {
    if (isMobile || !activeSubmenu) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return
      }
      // Focus may be on a link in the submenu that is about to be hidden, so move it to its trigger
      const trigger = navRef.current?.querySelector<HTMLElement>('[aria-expanded="true"]')
      if (navRef.current?.contains(document.activeElement)) {
        trigger?.focus()
      }
      setActiveSubmenu('')
      setIsSwitchingSubmenu(false)
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isMobile, activeSubmenu])

  const toggleSubmenu = (submenu: string, trackedEvent?: string) => {
    setActiveSubmenu((prev) => (prev === submenu ? '' : submenu))
    if (trackedEvent) analytics.track(trackedEvent)
  }

  if (isMobile) {
    return (
      <nav>
        <ul>
          <li className="item">
            <SubmenuLink
              isMobile
              label={t(services.label)}
              isActive={activeSubmenu === services.label}
              onClick={() => toggleSubmenu(services.label, services.trackedEvent)}
            >
              <ServicesSubmenu closeNavigation={closeMobileNavigation} />
            </SubmenuLink>
          </li>
          <li className="item">
            <SubmenuLink
              isMobile
              label={t(resources.label)}
              isActive={activeSubmenu === resources.label}
              onClick={() => toggleSubmenu(resources.label, resources.trackedEvent)}
            >
              <ResourcesSubmenu
                closeNavigation={closeMobileNavigation}
                items={resourcesSubmenuItems}
              />
            </SubmenuLink>
          </li>
          <li className="item">
            <NavigationLink
              isMobile
              label={t(about.label)}
              linkHref={about.linkHref}
              trackedEvent={about.trackedEvent}
              closeNavigation={closeMobileNavigation}
            />
          </li>
          <li className="item">
            <NavigationLink
              isMobile
              label={t(pricing.label)}
              linkHref={pricing.linkHref}
              trackedEvent={pricing.trackedEvent}
              closeNavigation={closeMobileNavigation}
            />
          </li>
        </ul>

        <style jsx>{`
          .item + .item {
            border-top: 1px solid ${theme.palette.gray[600]};
          }
        `}</style>
      </nav>
    )
  }

  return (
    <nav ref={navRef}>
      <ul className="list">
        <li
          onMouseEnter={() => showSubmenu(services.label, services.trackedEvent)}
          onMouseLeave={hideSubmenu}
          onBlur={(event) => handleDesktopBlur(event, services.label)}
        >
          <SubmenuLink
            label={t(services.label)}
            isActive={activeSubmenu === services.label}
            isSwitching={isSwitchingSubmenu}
            panelHeightRef={submenuPanelHeight}
            onClick={(event) => handleDesktopClick(event, services.label, services.trackedEvent)}
          >
            <ServicesSubmenu closeNavigation={closeDesktopNavigation} />
          </SubmenuLink>
        </li>
        <li
          onMouseEnter={() => showSubmenu(resources.label, resources.trackedEvent)}
          onMouseLeave={hideSubmenu}
          onBlur={(event) => handleDesktopBlur(event, resources.label)}
        >
          <SubmenuLink
            label={t(resources.label)}
            isActive={activeSubmenu === resources.label}
            isSwitching={isSwitchingSubmenu}
            panelHeightRef={submenuPanelHeight}
            onClick={(event) => handleDesktopClick(event, resources.label, resources.trackedEvent)}
          >
            <ResourcesSubmenu
              closeNavigation={closeDesktopNavigation}
              items={resourcesSubmenuItems}
            />
          </SubmenuLink>
        </li>
        <li>
          <NavigationLink
            label={t(about.label)}
            linkHref={about.linkHref}
            trackedEvent={about.trackedEvent}
          />
        </li>
        <li>
          <NavigationLink
            label={t(pricing.label)}
            linkHref={pricing.linkHref}
            trackedEvent={pricing.trackedEvent}
          />
        </li>
      </ul>

      <style jsx>{`
        .list {
          display: flex;
          align-items: center;
          position: relative;
          gap: ${theme.spacing[1]};
        }
      `}</style>
    </nav>
  )
}
